import _isEqual from 'lodash/isEqual'
import dynamic from 'next/dynamic'
import { memo, useMemo } from 'react'

import type { Color } from '@nxt-insurance/ui-kit/types'

import type { Section, Sections } from '@/cms/sections'
import { HeaderSection } from '@/components/renderers/HeaderTemplateSection'
import { MultiColumnSection } from '@/components/renderers/MultiColumnSection'
import { reportErrorMessage, reportWarning } from '@/services/errorReporting'
import { PortableText } from '@portabletext/react'
import type { PortableTextComponentProps, UnknownNodeType } from '@portabletext/react'

import { SectionRenderer } from './Section'
import { CoverTemplate } from './SectionTemplate/Cover'
import { FAQTemplate } from './SectionTemplate/FAQ'
import { HeaderTemplate } from './SectionTemplate/Header'
import { ProductsCarouselTemplate } from './SectionTemplate/ProductsCarousel'
import { USPTemplate } from './SectionTemplate/USP'

const PressArchive = dynamic(async () =>
  import('@/components/renderers/PressArchive').then((mod) => mod.PressArchive)
)
const ProductMenu = dynamic(async () =>
  import('@/components/renderers/ProductMenu').then((mod) => mod.ProductMenu)
)
const BlogOverview = dynamic(async () =>
  import('@/components/renderers/BlogOverview').then((mod) => mod.BlogOverview)
)

export const SectionsRenderer = memo(
  (props: { sections: Sections; backgroundColor?: Color; contentColor?: Color }) => {
    const components = useMemo(
      () => ({
        types: {
          'website.richContent.multiColumnSection': (
            node: PortableTextComponentProps<
              Exclude<
                Extract<Section, { name: string | null }>,
                { _type: 'website.richContent.section' }
              >
            >
          ) => (
            <MultiColumnSection
              index={node.index}
              section={node.value}
              backgroundColor={props.backgroundColor}
              contentColor={props.contentColor}
            />
          ),
          'website.richContent.headerSection': (
            node: PortableTextComponentProps<
              Exclude<
                Extract<
                  Section,
                  {
                    name: string | null
                    productTitle: string
                  }
                >,
                { _type: 'website.richContent.section' }
              >
            >
          ) => (
            <HeaderSection
              index={node.index}
              section={node.value}
              backgroundColor={props.backgroundColor}
            />
          ),
          'website.richContent.sharedSection': (
            node: PortableTextComponentProps<
              Exclude<
                Extract<Section, { name: string | null }>,
                { _type: 'website.richContent.section' }
              >
            >
          ) => (
            <MultiColumnSection
              index={node.index}
              section={node.value}
              backgroundColor={props.backgroundColor}
              contentColor={props.contentColor}
            />
          ),
          'website.richContent.sharedSection.ab.test': () => {
            throw new Error('Assertion: AB Test should be evaluated on the server')
          },
          'website.richContent.codedSection': (
            node: PortableTextComponentProps<
              Extract<Section, { _type: 'website.richContent.codedSection' }>
            >
          ) => {
            switch (node.value.type) {
              case 'productMenu':
                return (
                  <div className="flex w-full flex-col" id="section_productMenu">
                    <ProductMenu />
                  </div>
                )
              case 'blogOverview.posts':
                return (
                  <div className="flex w-full flex-col" id="section_blogOverview.posts">
                    <BlogOverview />
                  </div>
                )
              case 'press.archive':
                return (
                  <div className="flex w-full flex-col" id="section_press.archive">
                    <PressArchive />
                  </div>
                )
              default:
                reportWarning(`Rendering of ${node.value.type} not implemented yet`)
                return <div>{`Rendering of ${node.value.type} not implemented yet`}</div>
            }
          },
          'website.richContent.section': (
            node: PortableTextComponentProps<
              Extract<Section, { _type: 'website.richContent.section' }>
            >
          ) => {
            return (
              <SectionRenderer
                node={node}
                index={node.index}
                backgroundColor={props.backgroundColor}
                contentColor={props.contentColor}
              />
            )
          },
          'templates.header': (
            node: PortableTextComponentProps<Extract<Section, { _type: 'templates.header' }>>
          ) => {
            return <HeaderTemplate node={node} index={node.index} />
          },
          'templates.cover': (
            node: PortableTextComponentProps<Extract<Section, { _type: 'templates.cover' }>>
          ) => {
            return <CoverTemplate node={node} index={node.index} />
          },
          'templates.usp': (
            node: PortableTextComponentProps<Extract<Section, { _type: 'templates.usp' }>>
          ) => {
            return <USPTemplate node={node} index={node.index} />
          },
          'templates.productsCarousel': (
            node: PortableTextComponentProps<
              Extract<Section, { _type: 'templates.productsCarousel' }>
            >
          ) => {
            return <ProductsCarouselTemplate node={node} index={node.index} />
          },
          'templates.faq': (
            node: PortableTextComponentProps<Extract<Section, { _type: 'templates.faq' }>>
          ) => {
            return <FAQTemplate node={node} index={node.index} />
          },
        },
        unknownType: (node: PortableTextComponentProps<UnknownNodeType>) => (
          <div>Rendering of {node.value._type} not implemented yet</div>
        ),
      }),
      [props.backgroundColor, props.contentColor]
    )

    return (
      <PortableText
        value={props.sections}
        components={components}
        onMissingComponent={reportErrorMessage}
      />
    )
  },
  _isEqual
)
